import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { PaymentTable } from '@/components/PaymentTable';
import { useTenants } from '@/features/tenants/useTenants';
import {
  subscribeTenantPayments,
  type PaymentListResult,
} from '@/lib/firestoreTenantDetail';

const TENANT_FILTERS = { status: null } as const;

/**
 * Tela de pagamentos: escolhe a empresa num `Select` e lista os pagamentos
 * dela.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16 —
 * rotas /billing/* + sub-menu Cobrança).
 *
 * Por quê: pagamento é subcoleção do tenant; a visão cross-tenant
 * (collection-group) fica em `/finance/transactions`.
 *
 * Contrato: `useTenants` (listener de `tenants`, até 200) alimenta o
 * seletor, ordenado por nome; escolher navega para
 * `/billing/payments/:tenantId`, que assina `subscribeTenantPayments` (até
 * 100, `created_at desc`) e mostra no `PaymentTable`. Tenant da URL que não
 * está na lista aparece com o próprio id como rótulo. Só leitura.
 *
 * Usado por: `routes/router.tsx` (rotas `billing/payments` e
 * `billing/payments/:tenantId`, dentro do `RequireSuperAdmin`), menu
 * Cobrança → "Pagamentos" (`AdminLayout`). Só `apps/admin` (web). Spec:
 * `docs/screens/billing-payments.md`.
 *
 * Arquitetura: regra `match /payments/{paymentId}` sob `tenants/{tenantId}`
 * (Lê: membro ou super-admin; Escreve: só servidor — webhooks,
 * `recordManualPayment`, `requestRefund`). `orderBy` de campo único.
 *
 * Dados: `tenants/{tid}/payments/{id}` (`PaymentSchema`) — `amount` em
 * CENTAVOS (formatado pelo `MoneyDisplay`), `status` ∈ CONFIRMED | FAILED |
 * PROCESSING | REFUNDED | PARTIALLY_REFUNDED, `paid_at` `null` enquanto não
 * liquidado.
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function PaymentsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ tenantId?: string }>();
  const tenantId = params.tenantId ?? null;
  const [state, setState] = useState<PaymentListResult>({ kind: 'LOADING' });

  const tenantsState = useTenants(TENANT_FILTERS);
  const tenantOptions = useMemo<SelectProps.Option[]>(() => {
    if (tenantsState.kind !== 'READY') return [];
    return [...tenantsState.tenants]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((tenant) => ({ value: tenant.id, label: tenant.name, description: tenant.id }));
  }, [tenantsState]);

  const selectedOption = useMemo<SelectProps.Option | null>(() => {
    if (!tenantId) return null;
    return tenantOptions.find((o) => o.value === tenantId) ?? { value: tenantId, label: tenantId };
  }, [tenantId, tenantOptions]);

  useEffect(() => {
    if (!tenantId) {
      setState({ kind: 'READY', payments: [] });
      return;
    }
    setState({ kind: 'LOADING' });
    return subscribeTenantPayments(tenantId, setState);
  }, [tenantId]);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={tenantId ?? undefined}>
          {t('billing.payments.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <FormField label={t('billing.payments.selectTenant')}>
          <Select
            selectedOption={selectedOption}
            onChange={({ detail }): void => {
              const value = detail.selectedOption.value;
              if (value) void navigate(`/billing/payments/${value}`);
            }}
            options={tenantOptions}
            filteringType="auto"
            statusType={tenantsState.kind === 'LOADING' ? 'loading' : 'finished'}
            loadingText={t('common.loading', { defaultValue: 'Carregando…' })}
            placeholder={t('billing.payments.selectTenant')}
            empty={t('tenants.empty', { defaultValue: '—' })}
          />
        </FormField>

        {!tenantId ? (
          <Box color="text-body-secondary">{t('billing.payments.pickTenant')}</Box>
        ) : state.kind === 'LOADING' ? (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        ) : state.kind === 'ERROR' ? (
          <Alert type="error">{t('billing.payments.errorLoad')}</Alert>
        ) : (
          <PaymentTable
            payments={state.payments}
            locale={i18n.language}
            labels={{
              paidAt: t('billing.payments.columns.paidAt'),
              amount: t('billing.payments.columns.amount'),
              method: t('billing.payments.columns.method'),
              status: t('billing.payments.columns.status'),
              provider: t('billing.payments.columns.provider'),
              empty: t('billing.payments.empty'),
              notPaid: t('billing.payments.notPaid'),
              manual: t('billing.payments.manual'),
            }}
          />
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
